import { createAction } from "@reduxjs/toolkit";
import { createRsaaAction } from "../../components/utilities/ReduxUtils";
import type {
    ChemicalDataRequest,
    ChemicalDataResponse,
    ChemicalSearchRequest,
    ChemicalSearchResponse,
    WebSocketEstimateCompoundPayload,
    WebSocketEstimateCompoundSuccess,
    WebSocketEstimateCompoundUpdate,
    PocketbookFatModel,
} from "../../data/models/schemas";
import {
    createRealtimeRequestAction,
    createRealtimeRequestId,
    type RealtimeRequestAction,
} from "../middlewares/realtimeRequest";
import type { AppDispatch } from "../store";
import {
    refreshLicenseInfoMeta,
    withRemainingUnitsUpdateMeta,
} from "../utils/meteringMeta";
import type { IChemical, IReactionChemical } from "./library";
import { ESTIMATE_COMPOUND } from "./pocketbook";

// Action types
export const SHOW_CHEMICAL_INFO_MODAL = "SHOW_CHEMICAL_INFO_MODAL";
export const HIDE_CHEMICAL_INFO_MODAL = "HIDE_CHEMICAL_INFO_MODAL";
export const SET_CHEMICAL_INFO_MODAL_ACTIVE_TAB =
    "SET_CHEMICAL_INFO_MODAL_ACTIVE_TAB";
export const REQUEST_CHEMICAL_INFO_MODAL_ANALYSIS =
    "REQUEST_CHEMICAL_INFO_MODAL_ANALYSIS";

// Search, synchronous data loading, and asynchronous estimation actions
export const CHEMICAL_INFO_MODAL_SEARCH_COMPOUNDS =
    "CHEMICAL_INFO_MODAL_SEARCH_COMPOUNDS";
export const CHEMICAL_INFO_MODAL_LOAD_COMPOUNDS =
    "CHEMICAL_INFO_MODAL_LOAD_COMPOUNDS";
export const CHEMICAL_INFO_MODAL_ESTIMATE_COMPOUND =
    "CHEMICAL_INFO_MODAL_ESTIMATE_COMPOUND";

// Action interfaces
export interface IShowChemicalInfoModalAction {
    type: typeof SHOW_CHEMICAL_INFO_MODAL;
    payload: {
        chemical: IChemical | IReactionChemical;
    };
    meta?: Record<string, unknown>;
}

export interface IHideChemicalInfoModalAction {
    type: typeof HIDE_CHEMICAL_INFO_MODAL;
    meta?: Record<string, unknown>;
}

export interface ISetChemicalInfoModalActiveTabAction {
    type: typeof SET_CHEMICAL_INFO_MODAL_ACTIVE_TAB;
    payload: {
        activeTab: "basic-info" | "analysis";
    };
    meta?: Record<string, unknown>;
}

export interface IRequestChemicalInfoModalAnalysisAction {
    type: typeof REQUEST_CHEMICAL_INFO_MODAL_ANALYSIS;
    meta?: Record<string, unknown>;
}

type ISearchCompoundsRequest = ChemicalSearchRequest;
type ISearchCompoundsResponse = ChemicalSearchResponse;
type ILoadCompoundsRequest = ChemicalDataRequest;
type ILoadCompoundsResponse = ChemicalDataResponse;
export type IChemicalInfoModalEstimateResponse =
    WebSocketEstimateCompoundUpdate | WebSocketEstimateCompoundSuccess;
export type IChemicalInfoModalEstimateLifecyclePayload =
    IChemicalInfoModalEstimateResponse & {
        requestId: string;
    };

// Action creators
export const showChemicalInfoModal = createAction<
    IShowChemicalInfoModalAction["payload"]
>(SHOW_CHEMICAL_INFO_MODAL);

export const hideChemicalInfoModal = createAction(HIDE_CHEMICAL_INFO_MODAL);

export const setChemicalInfoModalActiveTab = createAction<
    ISetChemicalInfoModalActiveTabAction["payload"]
>(SET_CHEMICAL_INFO_MODAL_ACTIVE_TAB);

export const requestChemicalInfoModalAnalysis = createAction(
    REQUEST_CHEMICAL_INFO_MODAL_ANALYSIS
);

// API actions
export const chemicalInfoModalSearchCompounds = createRsaaAction<
    ISearchCompoundsRequest,
    ISearchCompoundsResponse,
    ISearchCompoundsRequest
>({
    path: "chemicals/search",
    method: "POST",
    type: CHEMICAL_INFO_MODAL_SEARCH_COMPOUNDS,
    meta: () => refreshLicenseInfoMeta,
});

export const chemicalInfoModalLoadCompounds = createRsaaAction<
    ILoadCompoundsRequest,
    ILoadCompoundsResponse,
    ILoadCompoundsResponse
>({
    path: "chemicals/data",
    method: "POST",
    type: CHEMICAL_INFO_MODAL_LOAD_COMPOUNDS,
    meta: (p) =>
        withRemainingUnitsUpdateMeta({
            clearCompounds: p.requestContext?.clearCompounds,
        }),
});

const chemicalInfoModalEstimateRequest = createAction<{ requestId: string }>(
    `${CHEMICAL_INFO_MODAL_ESTIMATE_COMPOUND}_REQUEST`
);
const chemicalInfoModalEstimateUpdate =
    createAction<IChemicalInfoModalEstimateLifecyclePayload>(
        `${CHEMICAL_INFO_MODAL_ESTIMATE_COMPOUND}_UPDATE`
    );
const chemicalInfoModalEstimateSuccess =
    createAction<IChemicalInfoModalEstimateLifecyclePayload>(
        `${CHEMICAL_INFO_MODAL_ESTIMATE_COMPOUND}_SUCCESS`
    );
const chemicalInfoModalEstimateError = createAction<{
    requestId: string;
    models?: PocketbookFatModel[];
}>(`${CHEMICAL_INFO_MODAL_ESTIMATE_COMPOUND}_ERROR`);

const estimateChemicalInfoModal =
    (payload: WebSocketEstimateCompoundPayload) =>
    async (dispatch: AppDispatch): Promise<void> => {
        const requestId = createRealtimeRequestId();
        let latestModels: PocketbookFatModel[] | undefined;
        dispatch(chemicalInfoModalEstimateRequest({ requestId }));

        try {
            const realtimeDispatch = dispatch as unknown as (
                action: RealtimeRequestAction<
                    WebSocketEstimateCompoundPayload,
                    WebSocketEstimateCompoundUpdate
                >
            ) => Promise<WebSocketEstimateCompoundSuccess>;
            const handleProgress = (
                update: WebSocketEstimateCompoundUpdate
            ): void => {
                if (update.models) {
                    latestModels = update.models;
                }
                dispatch(
                    chemicalInfoModalEstimateUpdate({
                        requestId,
                        ...update,
                    })
                );
            };
            const response = await realtimeDispatch(
                createRealtimeRequestAction<
                    WebSocketEstimateCompoundPayload,
                    WebSocketEstimateCompoundUpdate
                >({
                    type: ESTIMATE_COMPOUND,
                    responseTypePrefix: CHEMICAL_INFO_MODAL_ESTIMATE_COMPOUND,
                    requestId,
                    payload,
                    onRequest: handleProgress,
                    onUpdate: handleProgress,
                    meta: refreshLicenseInfoMeta,
                })
            );
            dispatch(
                chemicalInfoModalEstimateSuccess({
                    requestId,
                    ...response,
                })
            );
        } catch {
            dispatch(
                chemicalInfoModalEstimateError({
                    requestId,
                    models: latestModels,
                })
            );
        }
    };

export const chemicalInfoModalEstimateCompound = {
    action: estimateChemicalInfoModal,
    request: chemicalInfoModalEstimateRequest,
    update: chemicalInfoModalEstimateUpdate,
    success: chemicalInfoModalEstimateSuccess,
    error: chemicalInfoModalEstimateError,
};

// Union type for all actions
export type ChemicalInfoModalActions =
    | IShowChemicalInfoModalAction
    | IHideChemicalInfoModalAction
    | ISetChemicalInfoModalActiveTabAction
    | IRequestChemicalInfoModalAnalysisAction;
